/**
 * BATTMAN (Block Attribute Manager) mutations — Phase 2.4.
 *
 * AutoCAD BATTMAN is the dialog for managing ATTDEF entries inside a
 * block definition: list, edit properties, remove, reorder and sync.
 * This module exposes the pure reducer-friendly mutations that back
 * those operations. The dialog (BlockAttributeManagerDialog.tsx)
 * surfaces the UI; the reducer dispatches these mutations.
 *
 * Public surface (all re-exported by geometryMutations.ts):
 *   - removeAttdefMutation(input)    -> RemoveAttdefResult
 *   - updateAttdefMutation(input)    -> UpdateAttdefResult
 *   - renameAttdefTagMutation(input) -> RenameAttdefTagResult
 *   - reorderAttdefsMutation(input)  -> ReorderAttdefsResult
 *
 * Design notes
 * ------------
 * 1. Every mutation is a pure function over the passed `DrawingDocument`
 *    — it never mutates the input. Callers (the reducer) pipe the
 *    returned document through `assembleDrawingDocument` to apply
 *    defensive validation (Zod parse in dev, invariants check in prod).
 *
 * 2. ATTDEF operations cascade across INSERTs of the same block:
 *    - removeAttdefMutation with `removeFromInserts=true` drops every
 *      ATTRIB with a matching tag from each INSERT referencing the
 *      block. When `false`, the ATTRIB rows become orphans (kept in
 *      place so downstream ATTSYNC/etc. can reconcile later).
 *    - renameAttdefTagMutation rewrites every ATTRIB's `tag` field in
 *      the matching INSERTs to preserve the reference.
 *    - updateAttdefMutation touches the ATTDEF template only. ATTRIB
 *      rows inherit the template at render time (plus their optional
 *      overrides), so no cascade write is needed.
 *
 * 3. reorderAttdefsMutation requires the caller to provide the full
 *    list of ATTDEF ids for the block — matches AutoCAD's Move Up/Down
 *    buttons which operate on an in-memory reorder snapshot.
 */

import type {
  AttdefEntity,
  AttdefFlags,
  BlockDefinition,
  DrawingDocument,
} from "@dxf-ai-editor/drawing-model";

import {
  requireBlock,
  withBlockReplace,
  withInsertsOfBlock,
} from "./mutationHelpers";

// ═══════════════════════════════════════════════════════════════
// removeAttdefMutation
// ═══════════════════════════════════════════════════════════════

export interface RemoveAttdefInput {
  blockName: string;
  /** Id of the AttdefEntity to remove. */
  attdefId: string;
  /** When true, also drop ATTRIB rows with the same tag from every
   *  INSERT referencing the block. Default true (AutoCAD parity). */
  removeFromInserts: boolean;
  drawingDocument: DrawingDocument;
}

export interface RemoveAttdefResult {
  updatedDocument: DrawingDocument;
  /** Number of INSERT entities whose attributes array shrank. */
  removedFromInserts: number;
  summary: string;
}

export function removeAttdefMutation(
  input: RemoveAttdefInput,
): RemoveAttdefResult {
  const { blockName, attdefId, removeFromInserts, drawingDocument } = input;
  const blockDef = requireBlock(drawingDocument, blockName);
  const target = blockDef.entities.find(
    (e): e is AttdefEntity => e.type === "Attdef" && e.id === attdefId,
  );
  if (!target) {
    throw new Error(`ATTDEF ${attdefId} not found in block ${blockName}`);
  }
  const removedTag = target.tag;

  const docAfterBlockUpdate = withBlockReplace(
    drawingDocument,
    blockName,
    (b) => ({
      ...b,
      entities: b.entities.filter(
        (e) => !(e.type === "Attdef" && e.id === attdefId),
      ),
    }),
  );

  let updatedDocument = docAfterBlockUpdate;
  let removedFromInserts = 0;
  if (removeFromInserts) {
    const insertWalk = withInsertsOfBlock(
      docAfterBlockUpdate,
      blockName,
      (entity) => {
        const before = entity.attributes.length;
        const nextAttributes = entity.attributes.filter(
          (a) => a.tag !== removedTag,
        );
        return nextAttributes.length !== before
          ? { ...entity, attributes: nextAttributes }
          : entity;
      },
    );
    updatedDocument = insertWalk.doc;
    removedFromInserts = insertWalk.changedCount;
  }

  return {
    updatedDocument,
    removedFromInserts,
    summary: `ATTDEF ${removedTag} removed from ${blockName}${
      removeFromInserts
        ? ` (and ${removedFromInserts} insert${removedFromInserts === 1 ? "" : "s"})`
        : ""
    }`,
  };
}

// ═══════════════════════════════════════════════════════════════
// updateAttdefMutation
// ═══════════════════════════════════════════════════════════════

export interface UpdateAttdefInput {
  blockName: string;
  attdefId: string;
  updates: Partial<{
    prompt: string;
    defaultValue: string;
    height: number;
    rotation: number;
    widthFactor: number;
    obliqueAngle: number;
    styleName: string;
    hJustification: number;
    vJustification: number;
    flags: Partial<AttdefFlags>;
    backgroundFill: boolean;
    backgroundColor: string;
    fillBoxScale: number;
  }>;
  drawingDocument: DrawingDocument;
}

export interface UpdateAttdefResult {
  updatedDocument: DrawingDocument;
  summary: string;
}

export function updateAttdefMutation(
  input: UpdateAttdefInput,
): UpdateAttdefResult {
  const { blockName, attdefId, updates, drawingDocument } = input;
  const blockDef = requireBlock(drawingDocument, blockName);
  const target = blockDef.entities.find(
    (e): e is AttdefEntity => e.type === "Attdef" && e.id === attdefId,
  );
  if (!target) {
    throw new Error(`ATTDEF ${attdefId} not found in block ${blockName}`);
  }

  // Validation mirroring validateAttdefInput's ranges.
  if (updates.height !== undefined && updates.height <= 0) {
    throw new Error("ATTDEF height must be positive");
  }
  if (
    updates.obliqueAngle !== undefined &&
    (updates.obliqueAngle < -85 || updates.obliqueAngle > 85)
  ) {
    throw new Error("ATTDEF oblique angle must be in [-85, 85]");
  }
  if (
    updates.hJustification !== undefined &&
    (updates.hJustification < 0 || updates.hJustification > 5)
  ) {
    throw new Error("ATTDEF horizontal justification must be in [0, 5]");
  }
  if (
    updates.vJustification !== undefined &&
    (updates.vJustification < 0 || updates.vJustification > 3)
  ) {
    throw new Error("ATTDEF vertical justification must be in [0, 3]");
  }

  const nextFlags: AttdefFlags = updates.flags
    ? {
        invisible: updates.flags.invisible ?? target.flags.invisible,
        constant: updates.flags.constant ?? target.flags.constant,
        verify: updates.flags.verify ?? target.flags.verify,
        preset: updates.flags.preset ?? target.flags.preset,
        lockPosition: updates.flags.lockPosition ?? target.flags.lockPosition,
        multiline: updates.flags.multiline ?? target.flags.multiline,
      }
    : target.flags;

  const nextAttdef: AttdefEntity = {
    ...target,
    prompt: updates.prompt ?? target.prompt,
    defaultValue: updates.defaultValue ?? target.defaultValue,
    height: updates.height ?? target.height,
    rotation: updates.rotation ?? target.rotation,
    widthFactor: updates.widthFactor ?? target.widthFactor,
    obliqueAngle: updates.obliqueAngle ?? target.obliqueAngle,
    styleName: updates.styleName ?? target.styleName,
    hJustification: updates.hJustification ?? target.hJustification,
    vJustification: updates.vJustification ?? target.vJustification,
    flags: nextFlags,
    backgroundFill: updates.backgroundFill ?? target.backgroundFill,
    ...(updates.backgroundColor !== undefined
      ? { backgroundColor: updates.backgroundColor }
      : target.backgroundColor !== undefined
        ? { backgroundColor: target.backgroundColor }
        : {}),
    fillBoxScale: updates.fillBoxScale ?? target.fillBoxScale,
  };

  const updatedDocument = withBlockReplace(drawingDocument, blockName, (b) => ({
    ...b,
    entities: b.entities.map((e) =>
      e.type === "Attdef" && e.id === attdefId ? nextAttdef : e,
    ),
  }));

  return {
    updatedDocument,
    summary: `ATTDEF ${target.tag} updated in ${blockName}`,
  };
}

// ═══════════════════════════════════════════════════════════════
// renameAttdefTagMutation
// ═══════════════════════════════════════════════════════════════

export interface RenameAttdefTagInput {
  blockName: string;
  attdefId: string;
  newTag: string;
  drawingDocument: DrawingDocument;
}

export interface RenameAttdefTagResult {
  updatedDocument: DrawingDocument;
  /** Number of ATTRIB rows (across INSERTs) whose tag was rewritten. */
  cascadeCount: number;
  summary: string;
}

export function renameAttdefTagMutation(
  input: RenameAttdefTagInput,
): RenameAttdefTagResult {
  const { blockName, attdefId, newTag, drawingDocument } = input;
  const trimmed = newTag.trim();
  if (trimmed.length === 0) {
    throw new Error("New tag must not be empty");
  }

  const blockDef = requireBlock(drawingDocument, blockName);
  const target = blockDef.entities.find(
    (e): e is AttdefEntity => e.type === "Attdef" && e.id === attdefId,
  );
  if (!target) {
    throw new Error(`ATTDEF ${attdefId} not found in block ${blockName}`);
  }
  const oldTag = target.tag;
  if (oldTag === trimmed) {
    // No-op rename.
    return {
      updatedDocument: drawingDocument,
      cascadeCount: 0,
      summary: `ATTDEF tag unchanged`,
    };
  }
  // Uniqueness across the block.
  const duplicate = blockDef.entities.some(
    (e) => e.type === "Attdef" && e.id !== attdefId && e.tag === trimmed,
  );
  if (duplicate) {
    throw new Error(`Tag ${trimmed} already exists in block ${blockName}`);
  }

  const docAfterBlock = withBlockReplace(drawingDocument, blockName, (b) => ({
    ...b,
    entities: b.entities.map((e) =>
      e.type === "Attdef" && e.id === attdefId ? { ...e, tag: trimmed } : e,
    ),
  }));

  let cascadeCount = 0;
  const insertWalk = withInsertsOfBlock(docAfterBlock, blockName, (entity) => {
    let touched = false;
    const nextAttributes = entity.attributes.map((a) => {
      if (a.tag === oldTag) {
        touched = true;
        cascadeCount += 1;
        return { ...a, tag: trimmed };
      }
      return a;
    });
    return touched ? { ...entity, attributes: nextAttributes } : entity;
  });

  return {
    updatedDocument: insertWalk.doc,
    cascadeCount,
    summary: `ATTDEF tag renamed ${oldTag} -> ${trimmed} (cascade ${cascadeCount})`,
  };
}

// ═══════════════════════════════════════════════════════════════
// reorderAttdefsMutation
// ═══════════════════════════════════════════════════════════════

export interface ReorderAttdefsInput {
  blockName: string;
  /** New order. Must list every ATTDEF id in the block exactly once. */
  attdefIds: string[];
  drawingDocument: DrawingDocument;
}

export interface ReorderAttdefsResult {
  updatedDocument: DrawingDocument;
  summary: string;
}

export function reorderAttdefsMutation(
  input: ReorderAttdefsInput,
): ReorderAttdefsResult {
  const { blockName, attdefIds, drawingDocument } = input;
  const blockDef = requireBlock(drawingDocument, blockName);

  const presentAttdefs = blockDef.entities.filter(
    (e): e is AttdefEntity => e.type === "Attdef",
  );
  const presentIds = presentAttdefs.map((a) => a.id);
  const inputIds = [...attdefIds];

  if (inputIds.length !== presentIds.length) {
    throw new Error(
      `reorder requires all ATTDEF ids (expected ${presentIds.length}, got ${inputIds.length})`,
    );
  }
  const presentSet = new Set(presentIds);
  const inputSet = new Set(inputIds);
  if (presentSet.size !== inputSet.size) {
    throw new Error("reorder contains duplicate ATTDEF ids");
  }
  for (const id of inputIds) {
    if (!presentSet.has(id)) {
      throw new Error(`ATTDEF ${id} not in block ${blockName}`);
    }
  }

  // Rebuild entities array: non-Attdef entities keep their current
  // relative order; Attdef entities are reshuffled into the slots they
  // previously occupied, honouring the requested order.
  const attdefByid = new Map(presentAttdefs.map((a) => [a.id, a]));
  const reorderedAttdefs = inputIds
    .map((id) => attdefByid.get(id))
    .filter((a): a is AttdefEntity => Boolean(a));

  const nextEntities: BlockDefinition["entities"] = [];
  let attdefIndex = 0;
  for (const entity of blockDef.entities) {
    if (entity.type === "Attdef") {
      const next = reorderedAttdefs[attdefIndex];
      attdefIndex += 1;
      if (next) nextEntities.push(next);
    } else {
      nextEntities.push(entity);
    }
  }

  const updatedDocument = withBlockReplace(drawingDocument, blockName, (b) => ({
    ...b,
    entities: nextEntities,
  }));

  return {
    updatedDocument,
    summary: `ATTDEFs reordered in ${blockName}`,
  };
}
